import React, { useState, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import UserAddressService from '../../services/useraddress/useraddress.service';
import SalesOrderingService from '../../services/sales-ordering/sales-ordering-service';
import { AddressData, SalesOrderingAddressProps } from '../../models/sales-ordering/sales-ordering-model';
import './sales-ordering.css';

const SalesOrderingAddress: React.FC<SalesOrderingAddressProps> = ({
    visible,
    onHide,
    onAddressSaved,
    userId,
    toast
}) => {
    const [loading, setLoading] = useState(false);
    const [address, setAddress] = useState<AddressData>({
        addressLine1: '',
        addressLine2: '',
        landmark: '',
        pincode: '',
        areaId: '',
        stateId: 0,
        city: '',
        state: '',
        country: ''
    });
    const [areaOptions, setAreaOptions] = useState<any[]>([]);

    const handlePincodeSearch = useCallback(async (pincode?: string) => {
        const pincodeToSearch = pincode || address.pincode;
        if (!pincodeToSearch || pincodeToSearch.length < 6) return;

        setLoading(true);
        try {
            const response = await UserAddressService.getAddress(parseInt(pincodeToSearch));
            if (response && response.isSuccess) {
                setAreaOptions(response.areas || []);
                setAddress(prev => ({
                    ...prev,
                    city: response.districts?.[0]?.districtName || '',
                    stateId: Number(response.states?.[0]?.stateId ?? response.states?.[0]?.stateID ?? 0),
                    state: response.states?.[0]?.stateName || '',
                    country: response.countries?.[0]?.countryName || '',
                    areaId: response.areas?.length === 1 ? response.areas[0].areaId : ''
                }));
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Invalid Pincode' });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch location' });
        } finally {
            setLoading(false);
        }
    }, [address.pincode, toast]);

    const handleClose = useCallback(() => {
        setAddress({
            addressLine1: '',
            addressLine2: '',
            landmark: '',
            pincode: '',
            areaId: '',
            stateId: 0,
            city: '',
            state: '',
            country: ''
        });
        setAreaOptions([]);
        onHide();
    }, [onHide]);

    const handleSave = useCallback(async () => {
        if (!address.addressLine1 || !address.pincode) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Please fill required fields' });
            return;
        }

        if (!address.areaId) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Please select an area' });
            return;
        }

        if (!userId) {
            handleClose();
            toast.current?.show({ severity: 'info', summary: 'Address Buffered', detail: 'Address captured. Click Save to create customer and address.' });
            return;
        }

        setLoading(true);
        try {
            const payload = {
                userAddressID: 0,
                address1: address.addressLine1,
                address2: address.addressLine2,
                landmark: address.landmark,
                zipcode: address.pincode,
                userID: userId,
                areaId: Number(address.areaId),
                stateId: address.stateId,
                stateID: address.stateId,
                gpsLocation: null
            };

            const response = await UserAddressService.apiuserAddress1(payload);
            if (response && (response.isSuccess || response.userAddressID)) {
                toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Address added successfully' });
                handleClose();
                onAddressSaved(response.userAddressID, address);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to save address' });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Error saving address' });
        } finally {
            setLoading(false);
        }
    }, [address, userId, onAddressSaved, toast, handleClose]);

    return (
        <Dialog
            header="Add Address"
            visible={visible}
            onHide={handleClose}
            style={{ width: '50vw' }}
            breakpoints={{ '960px': '75vw', '641px': '95vw' }}
        >
            <div className="p-fluid grid formgrid">
                <div className="field col-12">
                    <label htmlFor="addressLine1" className="font-bold">Address Line 1 <span className="text-red-500">*</span></label>
                    <div className="p-input-icon-left w-full">
                        <i className="pi pi-home" />
                        <InputText
                            id="addressLine1"
                            value={address.addressLine1}
                            onChange={(e) => setAddress({ ...address, addressLine1: e.target.value })}
                            placeholder="Please Enter Address Line 1"
                        />
                    </div>
                </div>
                <div className="field col-12">
                    <label htmlFor="addressLine2" className="font-bold">Address Line 2</label>
                    <div className="p-input-icon-left w-full">
                        <i className="pi pi-home" />
                        <InputText
                            id="addressLine2"
                            value={address.addressLine2}
                            onChange={(e) => setAddress({ ...address, addressLine2: e.target.value })}
                            placeholder="Please Enter Address Line 2"
                        />
                    </div>
                </div>
                <div className="field col-12">
                    <label htmlFor="landmark" className="font-bold">Landmark</label>
                    <div className="p-input-icon-left w-full">
                        <i className="pi pi-map-marker" />
                        <InputText
                            id="landmark"
                            value={address.landmark}
                            onChange={(e) => setAddress({ ...address, landmark: e.target.value })}
                            placeholder="Please Enter Landmark"
                        />
                    </div>
                </div>
                <div className="field col-12 md:col-6">
                    <label htmlFor="pincode" className="font-bold">Pincode <span className="text-red-500">*</span></label>
                    <div className="p-input-icon-left w-full">
                        <i className="pi pi-map-marker" />
                        <InputText
                            id="pincode"
                            value={address.pincode}
                            onChange={(e) => {
                                const newPincode = e.target.value;
                                setAddress({ ...address, pincode: newPincode });

                                setAddress({ ...address, pincode: newPincode });
                                if (newPincode.length === 6) {
                                    handlePincodeSearch(newPincode);
                                }
                            }}
                            keyfilter="int"
                            maxLength={6}
                            placeholder="Please Enter Pincode"
                            className="w-full"
                        />
                    </div>
                </div>
                {areaOptions.length > 0 && (
                    <>
                        <div className="field col-12 md:col-6">
                            <label htmlFor="area" className="font-bold">Area</label>
                            <Dropdown
                                id="area"
                                value={address.areaId}
                                options={areaOptions}
                                optionLabel="areaName"
                                optionValue="areaId"
                                onChange={(e) => setAddress({ ...address, areaId: e.value })}
                                placeholder="Select Area"
                            />
                        </div>
                        <div className="field col-12 md:col-6">
                            <label className="font-bold">City</label>
                            <InputText value={address.city} disabled />
                        </div>
                        <div className="field col-12 md:col-6">
                            <label className="font-bold">State</label>
                            <InputText value={address.state} disabled />
                        </div>
                        <div className="field col-12 md:col-6">
                            <label className="font-bold">Country</label>
                            <InputText value={address.country} disabled />
                        </div>
                    </>
                )}
            </div>
            <div className="flex flex-column gap-3 mt-2">
                <Button label="Add Location" icon="pi pi-map-marker" className="p-button-outlined w-auto align-self-start" />
                <Button label="Add" className="p-button-success w-full" onClick={handleSave} loading={loading} />
            </div>
        </Dialog>
    );
};

export default SalesOrderingAddress;
